import {
  ActionModal,
  Bar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  ClockIcon,
  DateTimeField,
  DetailList,
  DetailListItem,
  Divider,
  FactList,
  LabelValueList,
  Masonry,
  TextField,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useRouter,
  useSnackbar,
  useTheme,
} from '@forgeglobal/design-framework'
import { CampaignHeader } from 'components/campaigns/CampaignHeader'
import { CampaignParticipants } from 'components/campaigns/CampaignParticipants'
import { CampaignParticipantsDetail } from 'components/campaigns/CampaignParticipantsDetail'
import { CampaignScheduleEmailsCard } from 'components/campaigns/CampaignScheduleEmailsCard'
import { Field, Form } from 'react-final-form'
import { InvestorStructureAbility } from 'util/Enums'
import {
  convertToCurrencyString,
  createAllocationBarCaption,
  createTemporalNoteFieldText,
  formatStructures,
} from 'components/campaigns/CampaignHelpers'

import { formatNumberComma, parseNumber } from 'util/NumberUtils'
import { useGetOfferingQuery, useUpdatePublishedOfferingMutation } from 'graphql/bindings/AdminBindings'
import React, { Fragment, useState } from 'react'
import moment from 'moment'
import numeral from 'numeral'

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    container: {
      padding: spacing(2),
    },
    performanceBar: {
      padding: spacing(2, 0),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    factList: {
      padding: spacing(2, 0, 4, 0),
    },
    temporalNote: {
      display: 'flex',
      alignItems: 'center',
      margin: spacing(2, 0),
    },
    temporalNoteText: {
      marginLeft: spacing(2),
    },
    performanceContainer: {
      width: spacing(32),
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    flexedBar: {
      marginLeft: spacing(2),
      minWidth: '50%',
    },
    performanceInfoText: {
      minWidth: '50%',
    },
    paymentFacts: {
      marginBottom: spacing(2),
    },
    detailListItem: {
      display: 'flex',
      alignItems: 'center',
    },
  })
)

export const Campaign: React.FC = () => {
  const { match } = useRouter<{ id: string }>()
  const campaignId = match.params.id
  const { data } = useGetOfferingQuery({ variables: { id: campaignId } })
  const classes = useStyles()
  const { palette } = useTheme()

  const offering = data?.offering
  const allocatedDollars = offering?.dollar_amount_allocated_gross

  const CampaignActivity = (
    <Card>
      <CardHeader title="Activity" />
      <CardContent surface="light">
        <div className={classes.factList}>
          <FactList
            facts={[
              {
                header: convertToCurrencyString(offering?.dollar_amount_at_starting_price_gross),
                subheader: 'total offering',
              },
              {
                header: convertToCurrencyString(offering?.dollar_amount_allocated_gross),
                subheader: 'allocated',
              },
              {
                header: convertToCurrencyString(
                  offering?.dollar_amount_at_starting_price_gross - offering?.dollar_amount_allocated_gross
                ),
                subheader: 'remaining',
              },
            ]}
          />
        </div>
        <Divider />
        <div className={classes.temporalNote}>
          <ClockIcon />
          <div className={classes.temporalNoteText}>
            <Typography variant="caption" color="textSecondary" fontWeight="light">
              {createTemporalNoteFieldText(offering?.status, offering?.available_to, offering?.available_from)}
            </Typography>
          </div>
        </div>
        <Divider />
        <div className={classes.performanceBar}>
          <Bar
            filled={(offering?.dollar_amount_allocated_gross / offering?.dollar_amount_at_starting_price_gross) * 100}
            minimum={(offering?.min_shares_to_close / offering?.num_available_shares) * 100}
            color={offering?.status !== 'Live' ? palette.text.secondary : palette.secondary.main}
            caption={createAllocationBarCaption(
              offering?.min_shares_to_close * offering?.starting_share_price,
              allocatedDollars
            )}
          />
        </div>
      </CardContent>
    </Card>
  )

  const offeringPerformance = [
    {
      label: 'Invited',
      value: offering?.num_invites_sent ?? 0,
    },
    {
      label: 'Opened',
      value: offering?.num_invites_opened ?? 0,
    },
    {
      label: 'Clicked',
      value: offering?.num_invites_clicked ?? 0,
    },
    {
      label: 'Signed NDA',
      value: offering?.num_ndas_signed ?? 0,
    },
    {
      label: 'Placed Bid',
      value: offering?.num_bids_placed ?? 0,
    },
    {
      label: 'Signed',
      value: offering?.num_orders_signed ?? 0,
    },
    {
      label: 'Paid',
      value: offering?.num_payments_received ?? 0,
    },
  ]

  const maximum = offeringPerformance.reduce((max, item) => {
    if (item.value > max) {
      max = item.value
    }
    return max
  }, 0)

  const CampaignPerformance = (
    <Card>
      <CardHeader title="Performance" />
      <CardContent surface="light">
        <Box mb={2}>
          <LabelValueList
            items={offeringPerformance.map(item => {
              const { label, value } = item
              const performancePercent = !Number.isNaN(value / maximum) ? value / maximum : 0
              return {
                label,
                value: (
                  <div className={classes.performanceContainer}>
                    <div className={classes.performanceInfoText}>
                      <Typography align="right">{`${value} (${(performancePercent * 100).toFixed(2)}%)`}</Typography>
                    </div>
                    <Bar
                      color={offering?.status !== 'Live' ? palette.text.secondary : palette.secondary.main}
                      filled={performancePercent * 100}
                      className={classes.flexedBar}
                    />
                  </div>
                ),
              }
            })}
          />
        </Box>
        <div className={classes.paymentFacts}>
          <FactList
            facts={[
              {
                header: numeral(offering?.dollar_amount_paid).format('$0.00a'),
                subheader: 'in payments received',
              },
              {
                header: numeral(offering?.dollar_amount_fees_paid).format('$0.00a'),
                subheader: 'collected in fees',
              },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  )

  interface CampaignFormValues {
    available_to?: string
    available_from?: string
    num_available_shares?: number
    min_listing_size?: number
    min_direct_size?: number
    min_shares_to_close?: number
    starting_share_price?: number
    commission?: number
    equifund_id?: number | null | undefined
    est_num_company_shares?: number
    valuation_type?: string | null | undefined
    custom_contact_message?: string | null | undefined
  }

  const initialValues = {
    available_from: offering?.available_from,
    available_to: offering?.available_to,
    num_available_shares: offering?.num_available_shares,
    min_listing_size: offering?.min_listing_size,
    min_direct_size: offering?.min_direct_size,
    min_shares_to_close: offering?.min_shares_to_close,
    starting_share_price: offering?.starting_share_price,
    commission: offering?.commission,
    equifund_id: offering?.equifund_id,
    est_num_company_shares: offering?.est_num_company_shares,
    valuation_type: offering?.valuation_type,
    custom_contact_message: offering?.custom_contact_message,
  }

  const validateForm = (values: CampaignFormValues) => {
    let errors: { [key: string]: string } = {}
    const available_from = moment(values.available_from)
    const available_to = moment(values.available_to)
    errors.available_to = ''
    errors.num_available_shares = ''

    if (!available_to.isValid()) {
      errors.available_to += 'End date is not a valid date. '
    }

    if (available_to.isBefore(available_from)) {
      errors.available_to += 'End date cannot be before start date. '
    }

    const num_available_shares = Number(values.num_available_shares)
    if (Number.isNaN(num_available_shares) || !Number.isInteger(num_available_shares) || num_available_shares < 1) {
      errors.num_available_shares += 'Number of shares needs to be a valid integer value. '
    }

    const min_shares_to_close = Number(values.min_shares_to_close)
    if (min_shares_to_close > num_available_shares) {
      errors.num_available_shares += 'Minimum shares to close must be less than available shares. '
    }
    const starting_share_price = Number(values.starting_share_price)
    const min_listing_size = Number(values.min_listing_size)
    if (min_listing_size > num_available_shares * starting_share_price) {
      errors.num_available_shares += 'Minimum listing size must be less than total volume. '
    }

    const min_direct_size = Number(values.min_direct_size)
    if (!Number.isNaN(min_direct_size) && min_direct_size > num_available_shares * starting_share_price) {
      errors.num_available_shares += 'Minimum direct size must be less than total volume. '
    }

    return errors
  }

  const CampaignSchedule = ({ available_to }: CampaignScheduleProps) => {
    const classes = useStyles()
    return (
      <Card>
        <CardHeader title="Schedule" />
        <CardContent surface="light">
          <DetailList>
            <DetailListItem
              label={moment(offering?.available_from).isBefore(new Date()) ? 'Started' : 'Starts'}
              content={moment(offering?.available_from).format('MMMM Do YYYY, h:mm:ss a')}></DetailListItem>
            <DetailListItem
              label={moment(available_to).isBefore(new Date()) ? 'Ended' : 'Ends'}
              content={<DateTimeField name="available_to" label="" />}
              className={classes.detailListItem}></DetailListItem>
          </DetailList>
        </CardContent>
      </Card>
    )
  }

  const useOfferingFormStyles = makeStyles(({ spacing, palette }: Theme) =>
    createStyles({
      fieldItem: {
        flex: '0 50%',
        paddingRight: spacing(2),
      },
      detailListItem: {
        display: 'flex',
        alignItems: 'center',
      },
    })
  )

  const CampaignTerms = () => {
    const classes = useOfferingFormStyles()
    return (
      <Card>
        <CardHeader title="Terms" />
        <CardContent surface="light">
          <DetailList>
            <DetailListItem
              label="Number of Shares"
              className={classes.detailListItem}
              content={
                <div className={classes.fieldItem}>
                  <Field
                    name="num_available_shares"
                    parse={parseNumber}
                    format={formatNumberComma}
                    component={TextField}
                    fullWidth
                  />
                </div>
              }></DetailListItem>
            <DetailListItem
              label="Share price (Gross)"
              content={numeral(offering?.starting_share_price).format('$0.00')}></DetailListItem>
            <DetailListItem label="Fees" content={numeral(offering?.commission).format('0[.]00%')}></DetailListItem>
            <DetailListItem
              label="Total offering amount"
              content={numeral(offering?.commission).format('0[.]00%')}></DetailListItem>
            <DetailListItem
              label="Minimum required to close"
              content={numeral(offering?.commission).format('0[.]00%')}></DetailListItem>
            <DetailListItem
              label="Minimum bid size"
              content={
                offering?.min_listing_size
                  ? numeral(offering?.min_listing_size)
                      .format('($0.00a)')
                      .toUpperCase()
                  : 'None'
              }></DetailListItem>
            <DetailListItem
              label="Minimum size for direct"
              content={
                offering?.min_direct_size
                  ? numeral(offering?.min_direct_size)
                      .format('($0.00a)')
                      .toUpperCase()
                  : 'None'
              }></DetailListItem>
            <DetailListItem
              label="Structure"
              content={formatStructures(
                offering?.possible_structures ?? [],
                InvestorStructureAbility
              )}></DetailListItem>
          </DetailList>
        </CardContent>
      </Card>
    )
  }
  const CampaignEmails = <CampaignScheduleEmailsCard offeringId={campaignId} />

  interface CampaignScheduleProps {
    available_to: string | undefined
  }

  interface CampaignTermsProps {
    num_available_shares: number | undefined
  }

  interface SectionsArgs {
    campaignScheduleProps: CampaignScheduleProps
    campaignTermsProps: CampaignTermsProps
  }

  const sections = ({ campaignScheduleProps, campaignTermsProps }: SectionsArgs) => [
    CampaignActivity,
    CampaignSchedule(campaignScheduleProps),
    CampaignTerms(),
    CampaignPerformance,
    CampaignEmails,
  ]

  const [updateOffering] = useUpdatePublishedOfferingMutation()
  const [open, isOpen] = useState(false)
  interface SaveDraftArgs {
    available_to: string | undefined
    campaignId: string | undefined
    num_available_shares: number | undefined
  }
  const { enqueueSnackbar } = useSnackbar()

  const saveDraftAndEnqueueSnackbar = ({ available_to, campaignId, num_available_shares }: SaveDraftArgs) => {
    const formData = {
      available_to: moment(available_to),
      id: campaignId,
      num_available_shares: num_available_shares,
    }
    updateOffering({
      variables: {
        form: formData,
      },
    }).then(() => {
      enqueueSnackbar('Offering successfully updated')
      isOpen(false)
    })
  }

  return (
    <Fragment>
      <Box className={classes.container}>
        <CampaignHeader type="singleItemPage" />
        <Box className={classes.container} mb={4}>
          <Form<CampaignFormValues>
            onSubmit={() => {}}
            initialValues={initialValues}
            validate={validateForm}
            keepDirtyOnReinitialize
            render={props => {
              const campaignScheduleProps = {
                available_to: props.values.available_to,
              }

              const campaignTermsProps = {
                num_available_shares: props.values.num_available_shares,
              }

              const saveDraftArgs = {
                available_to: props.values.available_to,
                campaignId: campaignId,
                num_available_shares: props.values.num_available_shares,
              }
              const errors = validateForm(props.values)
              const disabled = props.pristine || errors.available_to !== '' || errors.num_available_shares !== ''
              const editedFieldsMapping: { [key: string]: string } = {
                available_to: '"End Date"',
                num_available_shares: '"Number of Shares"',
              }
              const dirtyFields = Object.keys(props.dirtyFields)
              const editedFields = dirtyFields
                .map((key: string, index: number) => {
                  return editedFieldsMapping[key]
                })
                .join(', and ')
              return (
                <>
                  <ActionModal
                    open={open}
                    hideModal={() => isOpen(false)}
                    title="Confirm Update"
                    cancelAction={() => isOpen(false)}
                    confirmAction={() => saveDraftAndEnqueueSnackbar(saveDraftArgs)}
                    positiveMessage="Confirm"
                    negativeMessage="Cancel"
                    header="Are you sure?"
                    actionMessage={`You are about to update ${editedFields} on a live offering`}
                  />
                  <Box mb={3}>
                    <Button onClick={() => isOpen(true)} disabled={disabled}>
                      {props.dirty ? 'Update Offering' : 'Updated'}
                    </Button>
                  </Box>
                  <Masonry items={sections({ campaignScheduleProps, campaignTermsProps })} />
                </>
              )
            }}></Form>
          <CampaignParticipantsDetail offeringId={campaignId} />
          <CampaignParticipants offeringId={campaignId} />
        </Box>
      </Box>
    </Fragment>
  )
}
